/**
 * What a level-up hands over.
 *
 * Levels alternate: even ones grant a stat point, odd ones a perk point. Every level-up therefore
 * gives exactly one thing, which is the property that makes the curve legible without a table.
 *
 * The alternation is not arbitrary. Four stats with five points of headroom each is a ceiling of
 * twenty, and a stat point every level would run past it and start granting points nobody could
 * spend. Fifteen stat points at the cap leaves the ceiling out of reach, so a character is still
 * choosing which stat to feed on the last level rather than filling in the last blank.
 */

import { BASELINE_STAT, STAT_ORDER, statValue } from '@/sim/combat/statContract';
import { LEVEL_CAP } from '@/sim/progression/experienceCurve';
import type { StatBlock } from '@/sim/combat/statContract';

/** Stat points granted by having reached a level. Level two grants the first. */
export function statPointsFor(level: number): number {
  return Math.floor(clamp(level) / 2);
}

/** Perk points granted by having reached a level. Level three grants the first. */
export function perkPointsFor(level: number): number {
  return Math.floor((clamp(level) - 1) / 2);
}

/** Every stat point a character can ever be granted. */
export const TOTAL_STAT_POINTS = statPointsFor(LEVEL_CAP);

/** Every perk point a character can ever be granted. Deliberately short of the whole tree. */
export const TOTAL_PERK_POINTS = perkPointsFor(LEVEL_CAP);

/**
 * Stat points already spent, read off the block itself rather than stored beside it. A stat above
 * baseline was paid for, so the spend is the sum of the deviations and a respec is just a reset.
 * Nothing that can be recomputed is persisted, which is one fewer field a save can contradict.
 */
export function spentStatPoints(stats: StatBlock): number {
  let spent = 0;

  for (const id of STAT_ORDER) spent += Math.max(0, statValue(stats, id) - BASELINE_STAT);

  return spent;
}

function clamp(level: number): number {
  if (!Number.isFinite(level)) return 1;
  if (level < 1) return 1;
  if (level > LEVEL_CAP) return LEVEL_CAP;
  return Math.floor(level);
}
